import { z } from 'zod';
import { DamageSeverity } from './car-enums';
/**
 * Deal Score contract — one 0–100 number + A–D grade per lot, computed from
 * the platform's own lot data (AI damage analysis + lot completeness).
 * Stored in `ai_car_analysis.deal_score`; computed at ingest and refreshed
 * daily by the api cron.
 *
 * Weighted sum of normalized [0..1] components:
 *   damageRisk (62.5%): DamageSeverity → risk factor (see DEAL_SCORE_DAMAGE_RISK)
 *   dataCompleteness (37.5%): share of filled key lot fields
 *                             (photos, VIN, damage_description, expert report)
 *
 * The weights are the former damageRisk 25% / dataCompleteness 15% pair,
 * renormalized after the AutoRia-based margin (40%) and liquidity (20%)
 * components were removed on 2026-10-05 together with the AutoRia
 * integration. A component whose inputs are missing (no AI damage severity)
 * is EXCLUDED and the remaining weights are renormalized — a lot is never
 * punished for data the pipeline hasn't produced yet; missing *lot* data is
 * what dataCompleteness measures.
 *
 * All weights/thresholds are exported constants so calibration (acceptance
 * criterion: non-degenerate A–D distribution) is a constants-only change.
 */
/** Component weights — must sum to 1 (guarded by contract tests). */
export const DEAL_SCORE_WEIGHTS = {
    damageRisk: 0.625,
    dataCompleteness: 0.375,
};
/**
 * DamageSeverity → risk factor (1 = no risk discount, 0 = write-off).
 * The phase spec's {minor, moderate, severe, critical} is mapped onto the
 * canonical enum: none/minimal ≈ "minor", total_loss ≈ "critical";
 * `light` sits between minor and moderate.
 */
export const DEAL_SCORE_DAMAGE_RISK = {
    [DamageSeverity.NONE]: 1,
    [DamageSeverity.MINIMAL]: 1,
    [DamageSeverity.LIGHT]: 0.8,
    [DamageSeverity.MODERATE]: 0.6,
    [DamageSeverity.SEVERE]: 0.25,
    [DamageSeverity.TOTAL_LOSS]: 0,
};
/** Lower score bound (inclusive) per grade; below C's bound → D. */
export const DEAL_SCORE_GRADE_THRESHOLDS = { A: 75, B: 55, C: 35 };
export const DealScoreGradeSchema = z.enum(['A', 'B', 'C', 'D']);
/** Persisted shape of `ai_car_analysis.deal_score`. */
export const DealScoreSchema = z
    .object({
    score: z.number().min(0).max(100),
    grade: DealScoreGradeSchema,
    // Documents scored before 2026-10-05 may still carry `margin`/`liquidity`
    // until a refresh or `backfill:deal-score` rewrites them; zod drops those keys.
    components: z.object({
        damageRisk: z.number().min(0).max(1).nullable(),
        dataCompleteness: z.number().min(0).max(1),
    }),
    computed_at: z.coerce.date().nullish(),
})
    .passthrough();
function computeDamageRiskComponent(input) {
    const severity = input.damageSeverity;
    if (severity == null)
        return null;
    return DEAL_SCORE_DAMAGE_RISK[severity] ?? null;
}
function computeCompletenessComponent(input) {
    const flags = [
        input.hasPhotos,
        input.hasVin,
        input.hasDamageDescription,
        input.hasExpertReport,
    ];
    return flags.filter(Boolean).length / flags.length;
}
export function gradeForScore(score) {
    if (score >= DEAL_SCORE_GRADE_THRESHOLDS.A)
        return 'A';
    if (score >= DEAL_SCORE_GRADE_THRESHOLDS.B)
        return 'B';
    if (score >= DEAL_SCORE_GRADE_THRESHOLDS.C)
        return 'C';
    return 'D';
}
/**
 * Pure Deal Score computation. Deterministic: same input → same result;
 * no clock access (callers stamp `computed_at` when persisting).
 */
export function computeDealScore(input) {
    const components = {
        damageRisk: computeDamageRiskComponent(input),
        dataCompleteness: computeCompletenessComponent(input.completeness),
    };
    let weightedSum = 0;
    let weightTotal = 0;
    for (const key of Object.keys(DEAL_SCORE_WEIGHTS)) {
        const value = components[key];
        if (value == null)
            continue;
        weightedSum += DEAL_SCORE_WEIGHTS[key] * value;
        weightTotal += DEAL_SCORE_WEIGHTS[key];
    }
    const score = weightTotal > 0 ? Math.round((100 * weightedSum) / weightTotal) : 0;
    return { score, grade: gradeForScore(score), components };
}
//# sourceMappingURL=deal-score.js.map